import { useCallback, useRef } from 'react';

const usePasteUpload = (onFilePaste, acceptTypes = null, maxSize = 5 * 1024 * 1024) => {
  const pasteRef = useRef(null);

  // Helper function to check if file type is allowed
  const isFileTypeAllowed = (fileType, fileName) => {
    if (!acceptTypes || (Array.isArray(acceptTypes) && acceptTypes.length === 0)) {
      return true; // No restrictions
    }
    
    // Normalize acceptTypes to array
    let allowedTypes = [];
    if (typeof acceptTypes === 'string') {
      allowedTypes = acceptTypes.split(',').map(t => t.trim()).filter(t => t);
    } else if (Array.isArray(acceptTypes)) {
      allowedTypes = acceptTypes.map(t => typeof t === 'string' ? t.trim() : t).filter(t => t);
    }
    
    // If no valid types after normalization, allow all
    if (allowedTypes.length === 0) {
      return true;
    }
    
    // Check for wildcard
    if (allowedTypes.includes('*/*') || allowedTypes.includes('*')) {
      return true;
    }
    
    // Map of MIME types to extensions for pasted images
    const mimeToExt = {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip']
    };
    
    // Check if MIME type maps to an allowed extension
    if (mimeToExt[fileType]) {
      const extensions = mimeToExt[fileType];
      if (extensions.some(ext => allowedTypes.includes(ext))) {
        return true;
      }
    }
    
    // Check if file extension matches
    if (fileName) {
      const fileExt = '.' + fileName.split('.').pop().toLowerCase();
      if (allowedTypes.includes(fileExt)) {
        return true;
      }
    }
    
    // Check wildcard patterns (image/*, application/*, etc.)
    if (allowedTypes.includes('image/*') && fileType.startsWith('image/')) {
      return true;
    }
    if (allowedTypes.includes('application/*') && fileType.startsWith('application/')) {
      return true;
    }
    if (allowedTypes.includes('text/*') && fileType.startsWith('text/')) {
      return true;
    }
    
    // Check exact MIME type match
    if (allowedTypes.includes(fileType)) {
      return true;
    }
    
    return false;
  };

  const handlePaste = useCallback(async (event) => {
    event.preventDefault();
    
    const items = event.clipboardData?.items;
    if (!items) return;

    const files = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          // Check file size
          if (file.size > maxSize) {
            console.warn(`File ${file.name} is too large. Max size: ${maxSize / (1024 * 1024)}MB`);
            continue;
          }
          
          // Check file type if acceptTypes is specified
          if (!isFileTypeAllowed(file.type, file.name)) {
            console.warn(`File type ${file.type} (${file.name}) not allowed`);
            continue;
          }
          
          files.push(file);
        }
      } else if (item.kind === 'string' && item.type === 'text/html') {
        // Handle HTML content (e.g., copied from web pages)
        item.getAsString((htmlString) => {
          // Convert HTML to file if it contains image data
          const imgMatch = htmlString.match(/<img[^>]+src="data:([^"]+)"/);
          if (imgMatch) {
            const dataUrl = imgMatch[0].match(/src="([^"]+)"/)[1];
            const base64Data = dataUrl.split(',')[1];
            const mimeType = dataUrl.split(':')[1].split(';')[0];
            
            if (!isFileTypeAllowed(mimeType, 'pasted-image.png')) {
              console.warn(`Image type ${mimeType} not allowed`);
              return;
            }
            
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], `pasted-image-${Date.now()}.png`, { type: mimeType });
            
            if (file.size <= maxSize) {
              files.push(file);
            }
          }
        });
      }
    }
    
    if (files.length > 0) {
      onFilePaste(files);
    }
  }, [onFilePaste, acceptTypes, maxSize]);

  const setupPasteListener = useCallback((element) => {
    if (element) {
      pasteRef.current = element;
      element.addEventListener('paste', handlePaste);
      element.addEventListener('dragover', (e) => e.preventDefault());
      element.addEventListener('drop', (e) => e.preventDefault());
      
      // Add visual feedback
      element.addEventListener('dragenter', () => {
        element.classList.add('paste-active');
      });
      
      element.addEventListener('dragleave', () => {
        element.classList.remove('paste-active');
      });
      
      return () => {
        element.removeEventListener('paste', handlePaste);
        element.removeEventListener('dragover', (e) => e.preventDefault());
        element.removeEventListener('drop', (e) => e.preventDefault());
        element.removeEventListener('dragenter', () => {
          element.classList.add('paste-active');
        });
        element.removeEventListener('dragleave', () => {
          element.classList.remove('paste-active');
        });
      };
    }
  }, [handlePaste]);

  return { setupPasteListener };
};

export default usePasteUpload; 